import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environments';

/** Usuarios admin de pedidos MOBICO: en vez de la vista de solicitante ven el
 *  panel admin (segmentación 2026-07-13: se sumaron precios, expansión y
 *  gerencia comercial al aux de pedidos original). */
export const MOBICO_PIPES_ADMIN_EMAILS = [
  'aux.pedidos@mobico.com.mx',
  'expansion@mobico.com.mx',
  'g.comercial@mobico.com.mx',
  'pedidos@mobico.com.mx',
];

/** Correos SOLICITANTES del flujo de pipas (2026-07-22): el piloto se gatea
 *  por usuarios puntuales, no por grupo entero (pedidos.pipa.test es del grupo
 *  Garbri). Controla el menú y las páginas de estación del flujo; no afecta al
 *  panel admin (gateado por email, arriba). */
export const MOBICO_PIPES_REQUESTER_EMAILS = [
  'pedidos.pipa.test@gmail.com',
  // Solicitantes de Garbri (Edey 15-oct-2026).
  'claudiabravo@garbri.com.mx',
  'davidmorales@garbri.com.mx',
  'egarbri@hotmail.com',
  // Más solicitantes de Garbri (Daniel 20-oct-2026): estaciones 05598, 11381 y 12120, y ricardom.
  'f.zavala@garbri.com.mx',
  'i.solorio@garbri.com.mx',
  'a.cobian@garbri.com.mx',
  'ricardom@garbri.com.mx',
];

/** true si la sesión actual es un usuario solicitante del flujo de pipas. */
export function isMobicoPipesRequester(): boolean {
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const email = currentUser?.user?.email || currentUser?.user?.username || '';
    return MOBICO_PIPES_REQUESTER_EMAILS.includes(String(email).toLowerCase());
  } catch {
    return false;
  }
}

/** Códigos placeholder del catálogo entidades_federativas que no son un estado
 *  real (Nacional/Extranjero/No especificado) — mismos que excluye el back. */
const EXCLUDED_ENTIDAD_IDS = ['00', '88', '99'];

/** Nombres largos del catálogo → etiqueta corta (mismo diccionario que el
 *  shared select-entity de Inteligencia de Mercado). */
const STATE_SHORT_NAMES: { [name: string]: string } = {
  'Ciudad De México': 'CDMX',
  'Estado De México': 'Edo. Méx.',
  'Veracruz De Ignacio De La Llave': 'Veracruz',
  'Baja California Sur': 'B.C.S',
  'Coahuila De Zaragoza': 'Coahuila',
  'Michoacán De Ocampo': 'Michoacán',
};

/** Opciones del filtro por estado del flujo de pipas a partir del catálogo
 *  /general/entidades (via gasprecioService.getEntities()): excluye placeholders,
 *  acorta nombres largos y ordena alfabéticamente. value = entidad_id (string). */
export function mapStateFilterOptions(entities: any[]): { label: string; value: string }[] {
  const capitalize = (text: string) =>
    text.toLowerCase().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return (entities || [])
    .filter((e: any) => e && !EXCLUDED_ENTIDAD_IDS.includes(String(e.entidad_id)))
    .map((e: any) => {
      const name = capitalize(String(e.name || ''));
      return { label: STATE_SHORT_NAMES[name] || name, value: String(e.entidad_id) };
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/** Prefill de "nueva solicitud" lanzado desde una celda de Compra del
 *  cronograma de inventario (el (+) del listado de compras) o desde la vista
 *  "Sugerencia de Pedidos": el contexto ya sabe estación, producto y fecha
 *  requerida (y la sugerencia además la capacidad); el usuario solo captura
 *  PO y material. Hand-off de UNA sola lectura (ver consumeOrderPrefills). */
/** Acción que una vista deja para que otra instancia (modalOnly) abra su modal REAL: change/cancel = solicitante
 *  (app-pipes), approve/reject = respuesta del admin, admin-change = «Cambiar» del admin sobre un pedido aprobado. */
export type PendingActionType = 'change' | 'cancel' | 'approve' | 'reject' | 'admin-change';

export interface OrderPrefill {
  station: string;
  product: string;
  loadDate: string; // YYYY-MM-DD (formato del input type=date del modal)
  // Capacidad de pipa sugerida (litros). Solo la manda la sugerencia semanal;
  // el (+) del cronograma no la conoce y deja el select en placeholder.
  capacity?: number | null;
}

/** true si la sesión actual es un usuario admin de pedidos MOBICO. */
export function isMobicoPipesAdmin(): boolean {
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const email = currentUser?.user?.email || currentUser?.user?.username || '';
    return MOBICO_PIPES_ADMIN_EMAILS.includes(String(email).toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Servicio de Pedidos MOBICO (Fuel Orders).
 * Backend: /v1/gasprecio/fuel-orders (branch feature/mobico_inventario).
 * El BasicAuthInterceptor agrega el header Authorization automáticamente.
 */
@Injectable({ providedIn: 'root' })
export class FuelOrdersService {
  private endpoint = environment.apiUrl + '/v1/gasprecio/fuel-orders';
  private invEndpoint = environment.apiUrl + '/v1/gasprecio/inventory_managment';

  // Prefill pendiente del (+) del cronograma o de la sugerencia semanal (una
  // sola lectura). Lista: "Usar todas" de la sugerencia prellena N pipas.
  private orderPrefills: OrderPrefill[] | null = null;

  constructor(private http: HttpClient) {}

  /** El admin aprobó, rechazó o cambió un pedido desde su panel (Edey 19-oct: «en Cronograma no se ve, tengo que volver
   *  a hacer otro refresh»). Las vistas hermanas de la misma página (Cronograma y Sugerencias del app-pipes cronoOnly)
   *  se suscriben y recargan sin F5. */
  private ordersChanged = new Subject<void>();

  get ordersChanged$(): Observable<void> {
    return this.ordersChanged.asObservable();
  }

  notifyOrdersChanged(): void {
    this.ordersChanged.next();
  }

  set nextOrderPrefill(prefill: OrderPrefill) {
    this.orderPrefills = [prefill];
  }

  /** Prefill de VARIAS pipas ("Usar sugerencia"/"Usar todas" de la vista
   *  Sugerencia de Pedidos): una fila del formulario por elemento. */
  set nextOrderPrefillList(prefills: OrderPrefill[]) {
    this.orderPrefills = prefills?.length ? prefills : null;
  }

  /** Devuelve el prefill pendiente y lo limpia: el modal prellenado se abre una
   *  sola vez al llegar navegando; un F5 en Solicitudes no lo re-abre. */
  consumeOrderPrefill(): OrderPrefill | null {
    const prefills = this.consumeOrderPrefills();
    return prefills ? prefills[0] : null;
  }

  /** Versión lista completa del consume (modal multi-pipa prellenado). */
  /** Acción sobre un pedido PENDIENTE disparada desde los chips de Análisis de
   *  Pedido / Inventario R/T (propuesta pendientes 2026-08-03): app-pipes en
   *  modo modalOnly consume change/cancel (solicitante) y app-pipes-admin en
   *  modo modalOnly consume approve/reject (admin) — cada host abre solo uno
   *  de los dos, así que el hand-off de una sola lectura no se pisa. */
  private pendingAction: { type: PendingActionType; row: any } | null = null;

  set nextPendingAction(action: { type: PendingActionType; row: any }) {
    this.pendingAction = action;
  }

  consumePendingAction(): { type: PendingActionType; row: any } | null {
    const action = this.pendingAction;
    this.pendingAction = null;
    return action;
  }

  consumeOrderPrefills(): OrderPrefill[] | null {
    const prefills = this.orderPrefills;
    this.orderPrefills = null;
    return prefills;
  }

  /** Cronograma semanal (timeline): stock proyectado + volume_pending (pedidos) + volume_purchase (confirmados). */
  getTimeline(params: { group?: string; zones?: string; start_date?: string; end_date?: string }): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') { httpParams = httpParams.set(k, String(v)); }
    });
    return this.http.get(`${this.invEndpoint}/timeline`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Lista de pedidos de la semana por estación (estación ve solo lo suyo). */
  getWeekOrders(
    params: { group?: string; zones?: string; week?: number | string; product?: string } = {}
  ): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        httpParams = httpParams.set(k, String(v));
      }
    });
    return this.http.get(`${this.endpoint}/week`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Crea/reemplaza los pedidos de una estación+producto+semana. */
  saveOrders(body: any): Observable<any> {
    return this.http.post(`${this.endpoint}`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Edita una solicitud (pipa) existente. */
  updateFullOrder(id: string, body: any): Observable<any> {
    return this.http.put(`${this.endpoint}/${id}`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * MOBICO (admin) responde una solicitud: confirmed / rejected / review.
   * Al aprobar un pedido primario, delivery_date es la fecha de entrega
   * confirmada que captura el admin (YYYY-MM-DD). registration_date es la
   * fecha de registro en volumetría que captura la ESTACIÓN desde su
   * cronograma (PATCH sin status; el back la escribe en la compra ligada).
   */
  updateOrderStatus(id: string, body: { status?: string; mobico_response?: string; delivery_date?: string; liters?: number; total?: number; supply_price?: number; registration_date?: string; product?: string; as_change?: boolean }): Observable<any> {
    return this.http.patch(`${this.endpoint}/${id}`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Registra una solicitud de cambio sobre un pedido existente (doc hijo con
   * change_type: date | capacity | product | cancel y el valor nuevo).
   */
  requestChange(id: string, body: any): Observable<any> {
    return this.http.post(`${this.endpoint}/${id}/change`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Anticipación de una solicitud de cambio (regla MOBICO 48/24 hrs) calculada por el SERVIDOR con su hora de México,
   * no con el reloj de la computadora de quien captura. `dates` = fechas de entrega en juego (YYYY-MM-DD).
   * Regresa { state: 'ok' | 'waitlist' | 'late' | null, now }.
   */
  getChangeDeadline(dates: string[]): Observable<any> {
    return this.http.get(`${this.endpoint}/change-deadline`, { params: { dates: dates.join(',') } }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Pestaña «Pipas» del centro de notificaciones del header: admin MOBICO = lo que tiene por responder; solicitante =
   * lo que MOBICO respondió de sus estaciones. Regresa { role: 'admin' | 'requester' | null, items }.
   */
  getPipesNotifications(): Observable<{ role: string | null; items: any[] }> {
    return this.http.get(`${this.endpoint}/notifications`).pipe(
      map((r: any) => r?.data || { role: null, items: [] }),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Espacio del tanque por pipa para el aviso de capacidad del panel admin MOBICO (junta 27-oct). Mismo cálculo que el
   * pill «Espacio disp.» del Aprobar, en lote. Regresa [{ station, product, date, available_space, capacities }].
   */
  getSpaceCheck(items: { station: string; product: string; date: string }[]): Observable<any[]> {
    return this.http.post(`${this.endpoint}/space-check`, { items }).pipe(
      map((r: any) => r?.data || []),
      catchError((e) => throwError(e))
    );
  }

  /** Solicitudes de cambio de la semana agrupadas por tipo (addition/cancel/date/capacity/product).
   *  states = entidad_id (código de entidades_federativas); el back acepta string o array. */
  getChanges(
    // all_groups: 'true' = vista cross-grupo del panel admin MOBICO (el back
    // lo honra solo para los correos de MOBICO_PIPES_ADMIN_EMAILS).
    // all_stations: 'true' = scope a todas las estaciones del usuario sin
    // importar el grupo seleccionado (solicitante piloto multi-grupo).
    params: { group?: string; zones?: string; week?: string; product?: string; station?: string; states?: string; all_groups?: string; all_stations?: string } = {}
  ): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') { httpParams = httpParams.set(k, String(v)); }
    });
    return this.http.get(`${this.endpoint}/changes`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Borra una solicitud (pipa). */
  deleteOrder(id: string): Observable<any> {
    return this.http.delete(`${this.endpoint}/${id}`).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Sugerencia automática de litros/fecha para pre-llenar el formulario.
   * Con `date`, available_space viene proyectado a esa fecha de entrega (candado de
   * inventario); `exclude` ignora una pipa en el cálculo (la que se está editando).
   */
  /**
   * Sugerencia SEMANAL (motor suggest-week, back 2026-07-23): N pipas por
   * estación+producto para la semana objetivo, con riesgo, proyección día a
   * día y pedidos activos. Modo estación (station) o tablero (group/zones/
   * all_stations, mismos gates del back).
   */
  getSuggestWeek(
    params: { station?: string; group?: string; zones?: string; week?: string; product?: string; all_stations?: string } = {}
  ): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') { httpParams = httpParams.set(k, String(v)); }
    });
    return this.http.get(`${this.endpoint}/suggest-week`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  getSuggest(station: string, product: string, date?: string, exclude?: string): Observable<any> {
    let params = new HttpParams().set('station', station).set('product', product);
    if (date) { params = params.set('date', date); }
    if (exclude) { params = params.set('exclude', exclude); }
    return this.http.get(`${this.endpoint}/suggest`, { params }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }
}
